import {
  clearCheckoutPaymentIntent,
  updatePaymentMethod,
} from "@/api/rest/checkoutApi";
import { OutlineButton } from "@/components/StoreComponents/StoreButton";
import { StripeCheckoutInner } from "@/components/Stripe";
import { BrandclubStoreConfigUtils } from "@brandclub/common-ui";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCheckoutUI } from "../../CheckoutUIProvider";
import { PaymentMethodDisplay } from "./PaymentMethodDisplay";
import { CheckoutIntent } from "../../checkoutUIMachine";

const stripePromise = loadStripe(
  BrandclubStoreConfigUtils.getStripePublishableKey()
);

export const CheckoutEditPaymentForm = () => {
  const [state, send] = useCheckoutUI();

  const clientSecret =
    state.context.checkoutIntent?.stripePaymentIntent?.client_secret;

  if (!clientSecret || !state.context.checkoutIntent?.checkoutIntentId) {
    return <div>Loading...</div>;
  }

  if (state.context.checkoutIntent?.stripePaymentMethod) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <PaymentMethodDisplay
          paymentMethod={state.context.checkoutIntent.stripePaymentMethod}
        />
        <OutlineButton
          onClick={async () => {
            const { checkoutIntent, status, errorMessage } =
              await clearCheckoutPaymentIntent({
                checkoutIntentId:
                  state.context.checkoutIntent?.checkoutIntentId!,
              });
            if (status === "success") {
              send({ type: "SET_CHECKOUT_INTENT", data: { checkoutIntent } });
            } else {
              send({
                type: "SET_CHECKOUT_ERROR",
                data: { error: errorMessage },
              });
            }
          }}
          style={{
            marginTop: "10px",
          }}
        >
          Edit
        </OutlineButton>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        paymentMethodCreation: "manual",
      }}
    >
      <StripeCheckoutInner
        onShippingAddressChange={async (event) => {
          console.log("onShippingAddressChange", event);
          event.resolve();
        }}
        onShippingRateChange={async (event) => {
          console.log("onShippingRateChange", event);
          event.resolve();
        }}
        shippingRates={[
          {
            id: "standard",
            amount:
              state.context.checkoutIntent?.estimatedOrderSummary
                ?.shippingFees ?? 0,
            displayName: "2-5 business days",
            deliveryEstimate: {
              minimum: {
                unit: "business_day",
                value: 2,
              },
              maximum: {
                unit: "business_day",
                value: 5,
              },
            },
          },
        ]}
        onSuccess={async (paymentMethod, shippingAddress, billingDetails) => {
          let shippingAddressToSubmit: CheckoutIntent["shippingAddress"] =
            undefined;
          if (shippingAddress) {
            shippingAddressToSubmit = {
              address1: shippingAddress.address.line1,
              address2: shippingAddress.address.line2 ?? "",
              city: shippingAddress.address.city,
              state: shippingAddress.address.state,
              zip: shippingAddress.address.postal_code,
              country: shippingAddress.address.country,
              firstName: shippingAddress.name.split(" ")[0],
              lastName: shippingAddress.name.split(" ")[1],
              phoneNumber: billingDetails?.phone ?? "",
              email: billingDetails?.email ?? "",
            };
          }
          let billingAddressToSubmit: CheckoutIntent["billingAddress"] =
            undefined;
          if (billingDetails) {
            billingAddressToSubmit = {
              address1: billingDetails.address.line1,
              address2: billingDetails.address.line2 ?? "",
              city: billingDetails.address.city,
              state: billingDetails.address.state,
              zip: billingDetails.address.postal_code,
              country: billingDetails.address.country,
              firstName: billingDetails.name.split(" ")[0],
              lastName: billingDetails.name.split(" ")[1],
              email: billingDetails.email,
              phoneNumber: billingDetails.phone,
            };
          }
          const { checkoutIntent, status, errorMessage } =
            await updatePaymentMethod({
              checkoutIntentId: state.context.checkoutIntent?.checkoutIntentId!,
              paymentMethodId: paymentMethod.id,
              shippingAddress: shippingAddressToSubmit,
              billingAddress: billingAddressToSubmit,
            });

          if (status === "success") {
            send({
              type: "SET_CHECKOUT_INTENT",
              data: { checkoutIntent },
            });
          } else {
            send({
              type: "SET_CHECKOUT_ERROR",
              data: { error: errorMessage },
            });
          }
        }}
        onError={(error) => {
          console.error(error);
        }}
      />
    </Elements>
  );
};
